import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type DocumentDetailsVersion, type IdAndTitle } from "~/api/types";
import { useQuizzes } from "~/api/v2/useQuizzes";
import { useQuizTakers } from "~/api/v2/useQuizTakers";
import { Loader2 } from "lucide-react";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import InviteQuizTakersModal from "./InviteQuizTakersModal";
import { CreateQuizForVersion } from "./CreateQuizForVersion";
import { QuizTakersOverview } from "./QuizTakersOverview";

interface Props {
  documentVersion: DocumentDetailsVersion;
  category: IdAndTitle;
}

/**
 * Quiz management for one document version, shown alongside the readers
 * overview in the anchoring tab. Lets managers create the quiz, open it for
 * editing, invite takers, and follow up on who has completed it.
 */
export const QuizManagementSection = ({ documentVersion, category }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: quizzes, isLoading, error, mutate } = useQuizzes();
  const quiz = quizzes?.find(
    (candidate) => candidate.documentVersionId === documentVersion.id,
  );
  const isGenerating = quiz?.status === "generating";
  const takers = useQuizTakers(isGenerating ? undefined : quiz?.id);
  const [inviteOpen, setInviteOpen] = useState(false);

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (error) {
    return <ErrorState />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold">
          {t(($) => $.anchoring.manage.heading)}
        </h3>
        {!quiz && (
          <CreateQuizForVersion
            documentVersionId={documentVersion.id}
            onCreated={() => void mutate()}
          />
        )}
        {quiz && !isGenerating && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setInviteOpen(true)}>
              {t(($) => $.anchoring.actions.inviteTakers)}
            </Button>
            <Button onClick={() => void navigate(`/anchoring/${quiz.id}`)}>
              {t(($) => $.anchoring.manage.edit)}
            </Button>
          </div>
        )}
      </div>

      {isGenerating ? (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" />
          {t(($) => $.anchoring.manage.creating)}
        </p>
      ) : quiz ? (
        <QuizTakersOverview
          report={takers.data}
          isLoading={takers.isLoading}
          error={takers.error}
        />
      ) : (
        <p className="text-muted-foreground text-sm">
          {t(($) => $.anchoring.manage.empty)}
        </p>
      )}

      {quiz && !isGenerating && (
        <InviteQuizTakersModal
          quizzes={[{ quizId: quiz.id, category }]}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          onInvited={() => {
            void takers.mutate();
            void mutate();
          }}
        />
      )}
    </div>
  );
};
